import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Button, Card } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { userFetch, userSave } from '../../../actions';

function UserSettings(props) {
    const { t } = useTranslation();
    const { user, loading } = props;
    const dispatch = useDispatch();

    //  Formik configuration
    const formik = useFormik({
        initialValues: {...user},
        validationSchema: Yup.object().shape({
            firstNames: Yup.string()
                .required(t('form.first_name_required')),
            lastName: Yup.string()
                .required(t('form.last_name_required')),
            email: Yup.string()
                .email('form.email_invalid')
                .required(t('form.email_required')),
            password: Yup.string().min(6),
            phone: Yup.string()
				.min(7, t('form.7_characters'))
				.nullable(),
        }),
        enableReinitialize: true,
        onSubmit: values => {
            dispatch(userSave(user.userID, {...user, ...values}))
        },
    });
    const { values, errors, touched } = formik;

    const _fetchUser = React.useCallback((userID) =>  {
        if(userID) {
            dispatch(userFetch(userID))
        }
    }, [dispatch]);

    useEffect(() => {
         _fetchUser(user.userID)
    }, [user.userID, _fetchUser])

    return (
        <Row>
            <Col lg={8}>
                <Form onSubmit={formik.handleSubmit}>
                    <Card>
                        <Card.Header>
                            {t('settings.my_info')}
                        </Card.Header>
                        <Card.Body>
                            <Form.Group as={Row} controlId="firstNames" className="required">
                                <Form.Label column sm={3}>{t('general.first_name')}</Form.Label>
                                <Col sm={6}>
                                    <Form.Control
                                        onChange={formik.handleChange}
                                        value={values.firstNames}
                                        disabled={loading}
                                        isInvalid={errors.firstNames && touched.firstNames}
                                    />
                                    {errors.firstNames ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.firstNames}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="lastName" className="required">
                                <Form.Label column sm={3}>{t('general.last_name')}</Form.Label>
                                <Col sm={6}>
                                    <Form.Control
                                        onChange={formik.handleChange}
                                        value={values.lastName}
                                        disabled={loading}
                                        isInvalid={errors.lastName && touched.lastName}
                                    />
                                    {errors.lastName ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.lastName}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="email" className="required">
                                <Form.Label column sm={3}>{t('general.email')}</Form.Label>
                                <Col sm={6}>
                                    <Form.Control
                                        type="email"
                                        onChange={formik.handleChange}
                                        value={values.email}
                                        disabled={loading}
                                        isInvalid={errors.email && touched.email}
                                    />
                                    {errors.email ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.email}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="phone">
                                <Form.Label column sm={3}>{t('general.phone_number')}</Form.Label>
                                <Col sm={6}>
                                    <Form.Control
                                        type="tel"
                                        onChange={formik.handleChange}
                                        value={values.phone || ''}
                                        disabled={loading}
                                        isInvalid={errors.phone && touched.phone}
                                    />
                                    {errors.phone ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.phone}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="password">
                                <Form.Label column sm={3}>{t('general.password')}</Form.Label>
                                <Col sm={6}>
                                    <Form.Control
                                        type="password"
                                        onChange={formik.handleChange}
                                        value={values.password}
                                        disabled={loading}
                                        isInvalid={errors.password && touched.password}
                                        autoComplete="off"
                                    />
                                    {errors.password ? (
                                        <Form.Control.Feedback type="invalid">
                                            {errors.password}
                                        </Form.Control.Feedback>
                                    ) : null}
                                </Col>
                            </Form.Group>
                        </Card.Body>
                        <Card.Footer>
                            <div className="form-group row">
                                <div className="col-sm-9 offset-sm-3">
                                    <Button type="submit" variant="success" disabled={loading}>{t('general.save_changes')}</Button>
                                </div>
                            </div>
                        </Card.Footer>
                    </Card>
                </Form>
            </Col>
        </Row>
    );
}

UserSettings.propTypes = {
    user: PropTypes.object,
    loading: PropTypes.bool
}

export default UserSettings;